import { type HTMLAttributes, type PropsWithChildren, forwardRef } from 'react'
import { cn } from '#app/utils/tailwind-merge.ts'

type SeparatorProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>> & {
	orientation?: 'horizontal' | 'vertical'
	decorative?: boolean
}

/**
 * Separator component to visually or semantically separate content.
 *
 * @component
 * @example
 * ```jsx
 * <Separator className="my-4" />
 * ```
 *
 * @param {string} [orientation='horizontal'] - The orientation of the separator. Can be 'horizontal' or 'vertical'.
 * @param {decorative} [decorative] - Whether the separator is decorative or not.
 * @param {string} [className] - Additional CSS class names.
 * @param {React.HTMLAttributes<HTMLDivElement>} [props] - Additional HTML attributes.
 * @returns {JSX.Element} The Separator component.
 */
const Separator = forwardRef<HTMLDivElement, SeparatorProps>(({ orientation = 'horizontal', decorative, className, ...props }, forwardedRef) => {
	const ariaOrientation = orientation === 'vertical' ? orientation : undefined
	const semanticProps = decorative ? { role: 'none' } : { 'aria-orientation': ariaOrientation, role: 'separator' }

	return <div ref={forwardedRef} data-orientation={orientation} {...semanticProps} className={cn('separator', className)} {...props} />
})
Separator.displayName = 'Separator'

export { Separator }
